import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { KBD_SUBMIT_ATTR } from '@/hooks/formKeyboardNav'
import { cn } from '@/lib/utils'

type ConfirmDialogProps = {
	open: boolean
	onOpenChange: (open: boolean) => void
	title: React.ReactNode
	description?: React.ReactNode
	confirmLabel?: string
	cancelLabel?: string
	onConfirm?: () => void
	confirmVariant?: 'destructive' | 'default' | 'outline' | 'link' | 'secondary' | 'ghost'
	isLoading?: boolean
	/** Tags the dialog, and derives `-confirm` and `-cancel` for its two actions. */
	dataTestId?: string
	/** Raised above a host that creates its own stacking context, such as a popover. */
	contentClassName?: string
}

export function ConfirmDialog({
	open,
	onOpenChange,
	title,
	description,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	onConfirm,
	confirmVariant = 'destructive',
	isLoading = false,
	dataTestId,
	contentClassName,
}: ConfirmDialogProps) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className={cn('z-[999]', contentClassName)} data-testid={dataTestId}>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					{description && <DialogDescription>{description}</DialogDescription>}
				</DialogHeader>

				<DialogFooter>
					<Button
						variant='outline'
						onClick={() => onOpenChange(false)}
						data-testid={dataTestId && `${dataTestId}-cancel`}>
						{cancelLabel}
					</Button>
					<Button
						variant={confirmVariant}
						disabled={isLoading}
						data-testid={dataTestId && `${dataTestId}-confirm`}
						{...{ [KBD_SUBMIT_ATTR]: '' }}
						onClick={e => {
							e.preventDefault()
							onConfirm?.()
							onOpenChange(false)
						}}>
						{isLoading ? <Spinner className='mr-2' /> : null}
						{confirmLabel}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
